import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { blogPosts } from '../data/blogData';
import SEOHead from '../components/SEO/SEOHead';
import {
    Container,
    Typography,
    Grid,
    Card,
    CardContent,
    CardActions,
    Button,
    Chip,
    Box,
    InputBase,
    IconButton,
    Paper,
    alpha
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import AccessTimeIcon from '@mui/icons-material/AccessTime';

const BlogList = () => {
    const [searchTerm, setSearchTerm] = useState('');

    // Sort posts by date (newest first)
    const sortedPosts = [...blogPosts].sort((a, b) => new Date(b.date) - new Date(a.date));

    // Filter posts
    const filteredPosts = sortedPosts.filter(post =>
        post.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        post.tags.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase()))
    );

    // Featured post is the first one (if no search)
    const featuredPost = !searchTerm && filteredPosts.length > 0 ? filteredPosts[0] : null;
    const listPosts = featuredPost ? filteredPosts.slice(1) : filteredPosts;

    return (
        <>
            <SEOHead
                title="VerveAI Insights - AI Trends & Tutorials"
                description="Explore the latest in Artificial Intelligence. Expert articles, tutorials, and insights on ML, LLMs, and productivity tools."
                canonical="https://verveai.co/blog"
            />

            <Box sx={{
                bgcolor: '#030510',
                minHeight: '100vh',
                pb: 12,
                position: 'relative',
                overflow: 'hidden',
                '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: '-10%',
                    left: '50%',
                    width: '80%',
                    height: '800px',
                    transform: 'translate(-50%)',
                    background: 'radial-gradient(circle, rgba(0,122,255,0.1) 0%, rgba(0,230,118,0.05) 50%, rgba(0,0,0,0) 80%)',
                    zIndex: 0,
                    pointerEvents: 'none'
                }
            }}>

                {/* Header Section */}
                <Container maxWidth="lg" sx={{ pt: 16, pb: 8, position: 'relative', zIndex: 1 }}>
                    <Box sx={{ textAlign: 'center', mb: 8 }}>
                        <Chip
                            label="The Future of Work"
                            sx={{
                                mb: 3,
                                backgroundColor: alpha('#00E676', 0.1),
                                color: '#00E676',
                                fontWeight: 500,
                                border: `1px solid ${alpha('#00E676', 0.2)}`,
                                borderRadius: '12px',
                                px: 1
                            }}
                        />
                        <Typography
                            variant="h1"
                            sx={{
                                fontSize: { xs: '3rem', md: '5.5rem' },
                                fontWeight: 800,
                                mb: 2,
                                background: 'linear-gradient(135deg, #FFFFFF 0%, #B0B3C0 100%)',
                                WebkitBackgroundClip: 'text',
                                WebkitTextFillColor: 'transparent',
                                letterSpacing: '-0.02em'
                            }}
                        >
                            VerveAI <span style={{ color: '#007AFF', WebkitTextFillColor: '#007AFF' }}>Insights</span>
                        </Typography>
                        <Typography variant="h5" sx={{ color: '#8F9BB3', maxWidth: '600px', mx: 'auto', mb: 6, fontWeight: 300 }}>
                            Intelligent analysis for the automation age.
                        </Typography>

                        {/* Glass Search Bar */}
                        <Paper
                            elevation={0}
                            component="form"
                            sx={{
                                p: '2px 4px',
                                display: 'flex',
                                alignItems: 'center',
                                width: { xs: '95%', md: '500px' },
                                mx: 'auto',
                                borderRadius: '24px',
                                backgroundColor: alpha('#FFFFFF', 0.03),
                                border: `1px solid ${alpha('#FFFFFF', 0.1)}`,
                                backdropFilter: 'blur(20px)',
                                boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
                                transition: 'all 0.3s',
                                '&:focus-within': {
                                    borderColor: '#007AFF',
                                    boxShadow: '0 0 20px rgba(0,122,255,0.2)'
                                }
                            }}
                        >
                            <IconButton sx={{ p: '10px', color: '#B0B3C0' }}>
                                <SearchIcon />
                            </IconButton>
                            <InputBase
                                sx={{ ml: 1, flex: 1, color: '#fff' }}
                                placeholder="Search the knowledge base..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                            />
                        </Paper>
                    </Box>

                    {/* Featured Glass Card */}
                    {featuredPost && (
                        <Box component={Link} to={`/blog/${featuredPost.slug}`} sx={{ textDecoration: 'none', display: 'block', mb: 8 }}>
                            <Card
                                sx={{
                                    display: 'flex',
                                    flexDirection: { xs: 'column', md: 'row' },
                                    bgcolor: alpha('#151A26', 0.4),
                                    backdropFilter: 'blur(30px)',
                                    border: `1px solid ${alpha('#FFFFFF', 0.08)}`,
                                    borderRadius: '24px',
                                    overflow: 'hidden',
                                    transition: 'all 0.4s',
                                    '&:hover': {
                                        transform: 'translateY(-4px)',
                                        borderColor: alpha('#007AFF', 0.4),
                                        boxShadow: '0 20px 80px rgba(0,122,255,0.1)'
                                    }
                                }}
                            >
                                <Box sx={{ width: { xs: '100%', md: '55%' }, height: { xs: 300, md: 'auto' }, position: 'relative' }}>
                                    <Box
                                        component="img"
                                        src={featuredPost.image}
                                        alt={featuredPost.title}
                                        sx={{
                                            width: '100%',
                                            height: '100%',
                                            objectFit: 'cover',
                                        }}
                                    />
                                    <Box sx={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, background: 'linear-gradient(90deg, rgba(0,0,0,0) 60%, rgba(21,26,38,0.4) 100%)' }} />
                                </Box>
                                <Box sx={{ width: { xs: '100%', md: '45%' }, p: { xs: 4, md: 6 }, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                                    <Box sx={{ display: 'flex', gap: 1, mb: 3 }}>
                                        <Chip label="FEATURED" size="small" sx={{ bgcolor: '#007AFF', color: '#fff', fontWeight: 700, fontSize: '0.7rem' }} />
                                    </Box>
                                    <Typography variant="h3" sx={{ color: '#fff', fontWeight: 700, mb: 2, lineHeight: 1.1 }}>
                                        {featuredPost.title}
                                    </Typography>
                                    <Typography variant="body1" sx={{ color: '#8F9BB3', mb: 4, lineHeight: 1.6 }}>
                                        {featuredPost.excerpt}
                                    </Typography>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: '#00E676', fontWeight: 600 }}>
                                        Read Analysis <ArrowForwardIcon fontSize="small" />
                                    </Box>
                                </Box>
                            </Card>
                        </Box>
                    )}

                    {/* Glass Grid */}
                    <Grid container spacing={4}>
                        {listPosts.length > 0 ? (
                            listPosts.map((post, index) => {
                                // Logic for layout:
                                // Index 0, 1 (2nd & 3rd posts) -> md={6} (2 per row)
                                // Index 2+ (Rest) -> md={4} (3 per row)
                                const gridCols = index < 2 ? 6 : 4;

                                return (
                                    <Grid item key={post.id} xs={12} sm={6} md={gridCols}>
                                        <Box component={Link} to={`/blog/${post.slug}`} sx={{ textDecoration: 'none', height: '100%', display: 'block' }}>
                                            <Card
                                                sx={{
                                                    height: '100%',
                                                    display: 'flex',
                                                    flexDirection: 'column',
                                                    bgcolor: alpha('#151A26', 0.6),
                                                    backdropFilter: 'blur(40px)',
                                                    border: `1px solid ${alpha('#FFFFFF', 0.08)}`,
                                                    borderRadius: '24px',
                                                    overflow: 'hidden',
                                                    transition: 'all 0.4s ease-out',
                                                    '&:hover': {
                                                        transform: 'translateY(-8px)',
                                                        borderColor: '#007AFF',
                                                        boxShadow: '0 20px 40px rgba(0,122,255,0.15)',
                                                        '& .card-image': { transform: 'scale(1.08)' }
                                                    }
                                                }}
                                            >
                                                <Box sx={{ position: 'relative', height: 240, overflow: 'hidden' }}>
                                                    <Box
                                                        className="card-image"
                                                        sx={{
                                                            width: '100%',
                                                            height: '100%',
                                                            backgroundImage: `url(${post.image})`,
                                                            backgroundSize: 'cover',
                                                            backgroundPosition: 'center',
                                                            transition: 'transform 0.6s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
                                                        }}
                                                    />
                                                    <Box sx={{ position: 'absolute', inset: 0, background: 'linear-gradient(180deg, rgba(0,0,0,0) 0%, rgba(0,0,0,0.4) 100%)' }} />
                                                    <Box sx={{ position: 'absolute', top: 16, right: 16 }}>
                                                        <Chip
                                                            label={post.tags[0]}
                                                            size="small"
                                                            sx={{
                                                                bgcolor: 'rgba(255,255,255,0.1)',
                                                                backdropFilter: 'blur(8px)',
                                                                color: '#fff',
                                                                fontWeight: 600,
                                                                border: '1px solid rgba(255,255,255,0.2)'
                                                            }}
                                                        />
                                                    </Box>
                                                </Box>
                                                <CardContent sx={{ p: 3, flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                                                        <AccessTimeIcon sx={{ fontSize: 14, color: '#8F9BB3' }} />
                                                        <Typography variant="caption" sx={{ color: '#8F9BB3', fontWeight: 500 }}>
                                                            {post.date}
                                                        </Typography>
                                                    </Box>
                                                    <Typography
                                                        variant="h5"
                                                        sx={{
                                                            color: '#fff',
                                                            fontWeight: 700,
                                                            lineHeight: 1.3,
                                                            mb: 2,
                                                            display: '-webkit-box',
                                                            WebkitLineClamp: 2,
                                                            WebkitBoxOrient: 'vertical',
                                                            overflow: 'hidden',
                                                            minHeight: '2.6em' // fixes alignment
                                                        }}
                                                    >
                                                        {post.title}
                                                    </Typography>
                                                    <Typography
                                                        variant="body2"
                                                        sx={{
                                                            color: '#B0B3C0',
                                                            lineHeight: 1.6,
                                                            display: '-webkit-box',
                                                            WebkitLineClamp: 3,
                                                            WebkitBoxOrient: 'vertical',
                                                            overflow: 'hidden'
                                                        }}
                                                    >
                                                        {post.excerpt}
                                                    </Typography>
                                                </CardContent>
                                                <Box sx={{ px: 3, pb: 3, pt: 0 }}>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: '#007AFF', fontWeight: 600, fontSize: '0.9rem', transition: 'gap 0.2s', '&:hover': { gap: 1.5 } }}>
                                                        Read Article <ArrowForwardIcon sx={{ fontSize: 16 }} />
                                                    </Box>
                                                </Box>
                                            </Card>
                                        </Box>
                                    </Grid>
                                );
                            })
                        ) : (
                            <Box sx={{ width: '100%', textAlign: 'center', py: 8 }}>
                                <Typography variant="h6" color="text.secondary">
                                    No articles found.
                                </Typography>
                            </Box>
                        )}
                    </Grid>
                </Container>
            </Box>
        </>
    );
};

export default BlogList;
